import React from 'react';
import { ArrowUp, ArrowDown, LucideIcon } from 'lucide-react';

interface MetricsCardProps {
  title: string;
  value: string | number;
  change?: number;
  changeLabel?: string;
  icon: LucideIcon;
  color?: 'primary' | 'success' | 'warning' | 'info' | 'danger';
  trend?: 'up' | 'down' | 'neutral';
  loading?: boolean;
}

export default function MetricsCard({
  title,
  value,
  change,
  changeLabel,
  icon: Icon,
  color = 'primary',
  trend,
  loading = false,
}: MetricsCardProps) {
  const colorClasses = {
    primary: 'bg-blue-100 text-blue-700',
    success: 'bg-green-100 text-green-700',
    warning: 'bg-yellow-100 text-yellow-700',
    info: 'bg-indigo-100 text-indigo-700',
    danger: 'bg-red-100 text-red-700',
  };

  const trendColor = trend === 'up' ? 'text-green-600' : trend === 'down' ? 'text-red-600' : 'text-gray-600';

  if (loading) {
    return (
      <div className="card animate-pulse">
        <div className="flex items-start justify-between mb-4">
          <div className="w-12 h-12 bg-gray-200 rounded-lg"></div>
          <div className="w-16 h-4 bg-gray-200 rounded"></div>
        </div>
        <div>
          <div className="w-24 h-8 bg-gray-200 rounded mb-2"></div>
          <div className="w-32 h-4 bg-gray-200 rounded mb-1"></div>
          <div className="w-28 h-3 bg-gray-200 rounded"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="card card-hover">
      <div className="flex items-start justify-between mb-4">
        <div className={`p-2 rounded-lg ${colorClasses[color]}`}>
          <Icon className="w-5 h-5" />
        </div>
        {change !== undefined && (
          <span className={`flex items-center gap-1 text-xs font-medium ${trendColor}`}>
            {trend === 'up' && <ArrowUp className="w-3 h-3" />}
            {trend === 'down' && <ArrowDown className="w-3 h-3" />}
            {change > 0 && '+'}
            {change}%
          </span>
        )}
      </div>
      <div>
        <p className="text-2xl font-bold text-gray-900">{value}</p>
        <p className="text-sm text-gray-500 mt-1">{title}</p>
        {changeLabel && <p className="text-xs text-gray-400 mt-2">{changeLabel}</p>}
      </div>
    </div>
  );
}
